import React, { useRef, useState } from 'react';

import { PaperProps } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StoreSection } from 'components/SmartAssistantSections/StoreSection';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface StoreManagementSectionProps extends PaperProps {
  smartAssistant: SmartAssistant;
}

export function StoreManagementSection({ smartAssistant, ...paperProps }: StoreManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const hasChangesRef = useRef(false);
  const [selectedResourceStoreId, setSelectedResourceStoreId] = useState<string>(smartAssistant.shopifyStoreResource);

  const saveMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      hasChangesRef.current = false;
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_STORE');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_STORE')
  });

  const onSave = () => {
    saveMutation.mutate({
      id: smartAssistant._id,
      shopifyStoreResource: selectedResourceStoreId
    });
  };

  const onCancel = () => {
    setSelectedResourceStoreId(smartAssistant.shopifyStoreResource);
    hasChangesRef.current = false;
  };

  const onStoreSelect = (storeId: string) => {
    hasChangesRef.current = true;
    setSelectedResourceStoreId(storeId);
  };

  return (
    <ManageSmartAssistantSectionControlWrapper
      h={paperProps.h}
      title={t('SMART_ASSISTANT.RESOURCES_SECTION.STORE_TITLE')}
      hasChanges={hasChangesRef.current}
      loading={saveMutation.isLoading}
      onSave={onSave}
      onCancel={onCancel}
      withMarginBottom>
      <StoreSection
        h="100%"
        selectedResourceStoreId={selectedResourceStoreId}
        onStoreClick={onStoreSelect}
        {...paperProps}
      />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
